// src/components/Achievements.js
// Gamification: automatically computed badges from player data.
// Locked badges show progress to encourage return visits & longer sessions.
import React, { useMemo, useState } from 'react';

// Calculate the longest streak of consecutive days with races
const calculateMaxStreak = (data) => {
  if (!data || data.length === 0) return 0;
  const days = [...new Set(data.map((item) => item.date))].sort();
  let maxStreak = 1;
  let current = 1;
  for (let i = 1; i < days.length; i++) {
    const prev = new Date(days[i - 1]);
    const curr = new Date(days[i]);
    const diffDays = Math.round((curr - prev) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      current += 1;
      maxStreak = Math.max(maxStreak, current);
    } else {
      current = 1;
    }
  }
  return maxStreak;
};

const Achievements = ({ playerData }) => {
  const [showAll, setShowAll] = useState(false);
  const [hoveredBadge, setHoveredBadge] = useState(null);

  const achievements = useMemo(() => {
    if (!playerData || playerData.length === 0) return [];

    const totalRaces = playerData.length;
    const divisionData = playerData.filter((i) => i.division);
    const bestDivision = divisionData.length ? Math.min(...divisionData.map((i) => i.division)) : 99;
    const divWins = playerData.filter((i) => i.divisionRank === 1).length;
    const top10Count = playerData.filter((i) => i.percentile && i.percentile <= 10).length;
    const top5Count = playerData.filter((i) => i.percentile && i.percentile <= 5).length;
    const podiums = playerData.filter((i) => i.divisionRank && i.divisionRank <= 3).length;
    const maxStreak = calculateMaxStreak(playerData);
    const bigLobbies = playerData.filter((i) => i.totalPlayers && i.totalPlayers >= 3000).length;

    return [
      {
        id: 'first-race', icon: '🏁', title: 'First Steps', desc: 'Complete your first COTD',
        unlocked: totalRaces >= 1, progress: Math.min(totalRaces, 1), goal: 1, color: '#3b82f6',
      },
      {
        id: 'regular', icon: '🎖️', title: 'Regular', desc: 'Complete 25 COTDs',
        unlocked: totalRaces >= 25, progress: Math.min(totalRaces, 25), goal: 25, color: '#3b82f6',
      },
      {
        id: 'veteran', icon: '🏆', title: 'Veteran', desc: 'Complete 100 COTDs',
        unlocked: totalRaces >= 100, progress: Math.min(totalRaces, 100), goal: 100, color: '#8b5cf6',
      },
      {
        id: 'legend', icon: '👑', title: 'Legend', desc: 'Complete 250 COTDs',
        unlocked: totalRaces >= 250, progress: Math.min(totalRaces, 250), goal: 250, color: '#f59e0b',
      },
      {
        id: 'div-winner', icon: '🥇', title: 'Division Winner', desc: 'Win your division',
        unlocked: divWins >= 1, progress: Math.min(divWins, 1), goal: 1, color: '#f59e0b',
      },
      {
        id: 'serial-winner', icon: '🏅', title: 'Serial Winner', desc: 'Win your division 5 times',
        unlocked: divWins >= 5, progress: Math.min(divWins, 5), goal: 5, color: '#f59e0b',
      },
      {
        id: 'podium-collector', icon: '🍾', title: 'Podium Collector', desc: '10 division podiums (Top 3)',
        unlocked: podiums >= 10, progress: Math.min(podiums, 10), goal: 10, color: '#ec4899',
      },
      {
        id: 'elite', icon: '💎', title: 'Elite Player', desc: 'Reach Division 3 or better',
        unlocked: bestDivision <= 3, progress: bestDivision <= 3 ? 1 : 0, goal: 1, color: '#8b5cf6',
      },
      {
        id: 'div1', icon: '⭐', title: 'Division 1', desc: 'Reach the top division',
        unlocked: bestDivision === 1, progress: bestDivision === 1 ? 1 : 0, goal: 1, color: '#f59e0b',
      },
      {
        id: 'top10', icon: '🎯', title: 'Sharpshooter', desc: 'Finish in the Top 10% five times',
        unlocked: top10Count >= 5, progress: Math.min(top10Count, 5), goal: 5, color: '#10b981',
      },
      {
        id: 'top5', icon: '🚀', title: 'Rocket', desc: 'Finish in the Top 5%',
        unlocked: top5Count >= 1, progress: Math.min(top5Count, 1), goal: 1, color: '#10b981',
      },
      {
        id: 'streak3', icon: '🔥', title: 'On Fire', desc: 'Play 3 days in a row',
        unlocked: maxStreak >= 3, progress: Math.min(maxStreak, 3), goal: 3, color: '#ef4444',
      },
      {
        id: 'streak7', icon: '☄️', title: 'Unstoppable', desc: 'Play 7 days in a row',
        unlocked: maxStreak >= 7, progress: Math.min(maxStreak, 7), goal: 7, color: '#ef4444',
      },
      {
        id: 'big-lobby', icon: '🌍', title: 'Big Stage', desc: 'Race in a 3000+ player COTD',
        unlocked: bigLobbies >= 1, progress: Math.min(bigLobbies, 1), goal: 1, color: '#06b6d4',
      },
    ];
  }, [playerData]);

  if (!achievements.length) return null;

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const visibleAchievements = showAll ? achievements : achievements.slice(0, 7);

  return (
    <div className="mb-8 animate-fade-in-up">
      <div className="rounded-2xl p-5" style={{
        background: 'var(--color-glass)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--color-glassBorder)',
      }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🏅</span>
            <h3 className="text-lg font-bold" style={{ color: 'var(--color-textPrimary)' }}>
              Achievements
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-textInverse)',
            }}>
              {unlockedCount}/{achievements.length}
            </span>
          </div>
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm font-medium px-3 py-1 rounded-lg transition-all hover:scale-[1.03]"
            style={{ color: 'var(--color-primary)' }}
          >
            {showAll ? 'Show less ↑' : 'Show all ↓'}
          </button>
        </div>

        {/* Overall progress bar */}
        <div className="w-full rounded-full h-1.5 mb-4" style={{ backgroundColor: 'var(--color-backgroundSecondary)' }}>
          <div
            className="h-1.5 rounded-full transition-all duration-700"
            style={{
              width: `${(unlockedCount / achievements.length) * 100}%`,
              background: 'linear-gradient(to right, var(--color-gradientFrom), var(--color-gradientTo))',
            }}
          />
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
          {visibleAchievements.map((badge) => (
            <div
              key={badge.id}
              className="relative text-center p-3 rounded-xl cursor-pointer transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: badge.unlocked ? `${badge.color}15` : 'var(--color-backgroundSecondary)',
                border: `1px solid ${badge.unlocked ? badge.color + '40' : 'var(--color-border)'}`,
                opacity: badge.unlocked ? 1 : 0.55,
              }}
              onMouseEnter={() => setHoveredBadge(badge.id)}
              onMouseLeave={() => setHoveredBadge(null)}
            >
              <div
                className="text-2xl mb-1 transition-transform duration-300"
                style={{
                  filter: badge.unlocked ? 'none' : 'grayscale(1)',
                  transform: hoveredBadge === badge.id && badge.unlocked ? 'scale(1.2) rotate(-8deg)' : 'scale(1)',
                }}
              >
                {badge.icon}
              </div>
              <div className="text-[11px] font-semibold leading-tight" style={{ color: 'var(--color-textPrimary)' }}>
                {badge.title}
              </div>

              {/* Progress for locked badges */}
              {!badge.unlocked && badge.goal > 1 && (
                <div className="mt-1.5">
                  <div className="w-full rounded-full h-1" style={{ backgroundColor: 'var(--color-border)' }}>
                    <div
                      className="h-1 rounded-full"
                      style={{
                        width: `${(badge.progress / badge.goal) * 100}%`,
                        backgroundColor: badge.color,
                      }}
                    />
                  </div>
                  <div className="text-[9px] mt-0.5" style={{ color: 'var(--color-textMuted)' }}>
                    {badge.progress}/{badge.goal}
                  </div>
                </div>
              )}

              {/* Tooltip */}
              {hoveredBadge === badge.id && (
                <div
                  className="absolute z-20 left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-xl pointer-events-none"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-textPrimary)',
                  }}
                >
                  {badge.desc}
                  {!badge.unlocked && <span style={{ color: 'var(--color-textMuted)' }}> (locked)</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Achievements;
